import { useEffect, useState } from "react";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";
import { forgetpassSendOtp, verifyOtp, updatePassword, Base_Url } from "../../../../config/config";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {

  const [step, setStep] = useState("email"); // email → otp → password

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({
      duration: 1200,
      once: true,
    });
  }, []);

  // ✅ SEND OTP
  const handleSendOtp = async () => {
    if (!email) return toast.error("Enter email");

    try {
      setLoading(true);

      const res = await axios.post(Base_Url + forgetpassSendOtp, { email });

      if (res.data?.status === "success") {
        toast.success(res.data.message);
        setStep("otp");
      } else {
        toast.error(res.data.message);
      }

    } catch (err) {
      toast.error(err.response?.data?.message || "Error sending OTP");
    } finally {
      setLoading(false);
    }
  };

  // ✅ VERIFY OTP
  const handleVerifyOtp = async () => {
    if (!otp) return toast.error("Enter OTP");

    try {
      setLoading(true);

      const res = await axios.post(Base_Url + verifyOtp, { email, otp });

      if (res.data?.status === "success") {
        toast.success(res.data.message);
        setStep("password");
      } else {
        toast.error(res.data.message);
      }

    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  // ✅ UPDATE PASSWORD
  const handleUpdatePassword = async () => {
    if (newPassword !== confirmPassword) {
      return toast.error("Passwords do not match");
    }

    try {
      setLoading(true);

      const res = await axios.post(Base_Url + updatePassword, {
        email,
        newpassword: newPassword,
        confirmpassword: confirmPassword,
      });

      if (res.data?.status === "success") {
        toast.success("Password updated");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        toast.error(res.data.message);
      }

    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-around relative overflow-hidden">

      {/* BG */}
      <div className="absolute inset-0">
        <img
          src="assets/images/login-bg.png"
          alt="bg"      
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-10 grid lg:grid-cols-2 gap-10 w-full max-w-6xl px-6">

        {/* LEFT SIDE SAME (NO CHANGE) */}

        <div
          className="space-y-8"
          data-aos="fade-right"
          data-aos-duration="1200"
        >

          <h1
            className="text-4xl font-bold text-gray-800 leading-tight"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Delivering Excellence, <br />
            <span className="text-red-500">Driving Growth.</span>
          </h1>

          <p
            className="text-gray-600"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Smart logistics solutions to move your business forward.
          </p>

          {/* FEATURES */}
          <div className="space-y-6 mt-6">
            {[
              { title: "Fast Delivery", desc: "Timely shipments worldwide." },
              { title: "Secure Handling", desc: "Safe order handling." },
              { title: "Smart Solutions", desc: "Tech-driven logistics." },
            ].map((item, i) => (
              <div
                key={i}
                data-aos="fade-up"
                data-aos-delay={300 + i * 150}
                className="flex gap-4 items-start group transition-all duration-300 hover:translate-x-2"
              >
                {/* ICON */}
                <div className="w-10 h-10 border border-red-400 rounded-lg flex items-center justify-center text-red-500 
                        group-hover:bg-red-500 group-hover:text-white transition-all duration-300">
                  🚚
                </div>

                {/* TEXT */}
                <div>
                  <h3 className="font-semibold text-gray-800 group-hover:text-red-500 transition">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT CARD */}
        <div className="flex justify-center items-center">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border p-8">

            <div className="text-center mb-4">
              <img src="assets/images/logo1.png" className="mx-auto w-48 " />
            </div>

            {/* ✅ STEP TITLES */}
            <h2 className="text-xl font-semibold text-center mb-4">
              {step === "email" && "Forgot your password?"}
              {step === "otp" && "Verify OTP"}
              {step === "password" && "Set New Password"}
            </h2>

            {/* ================= EMAIL ================= */}
            {step === "email" && (
              <>
                <div className="flex items-center border rounded-lg px-3 py-3 mb-4">
                  <Mail className="text-gray-400 mr-2" size={18} />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 outline-none"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <button
                  onClick={handleSendOtp}
                  disabled={loading}
                  className="w-full bg-red-500 text-white py-3 rounded-lg"
                >
                  {loading ? "Sending..." : "Send OTP"}
                </button>
              </>
            )}

            {/* ================= OTP ================= */}
            {step === "otp" && (
              <>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  className="w-full border rounded-lg px-3 py-3 mb-4"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />

                <button
                  onClick={handleVerifyOtp}
                  disabled={loading}
                  className="w-full bg-red-500 text-white py-3 rounded-lg"
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>
              </>
            )}

            {/* ================= PASSWORD ================= */}
            {step === "password" && (
              <>
                <div className="relative mb-4">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="New Password"
                    className="w-full border px-3 py-3 rounded-lg"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3"
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>

                <input
                  type="password"
                  placeholder="Confirm Password"
                  className="w-full border px-3 py-3 rounded-lg mb-4"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />

                <button
                  onClick={handleUpdatePassword}
                  disabled={loading}
                  className="w-full bg-red-500 text-white py-3 rounded-lg"
                >
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </> 
            )}

            <p className="text-center text-sm mt-4">
              <Link to="/login" className="text-blue-500">
                Back to Login
              </Link>
            </p>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;